import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import {formatValueForTable} from '../../utils/formatValueForTable';

const FoodSpecificationViewResult = ({ result }) => {
  // Return a fallback UI if result is missing or malformed
  if (!result || typeof result !== 'object' || !result.analysis) {
    return (
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-center gap-2 text-error">
          <PhosphorIcons.Warning size={20} weight="bold" />
          <p className="font-semibold">No valid food specification data available</p>
        </div>
      </div>
    );
  }

  // Format confidence score
  const formatConfidence = (confidence) => {
    if (typeof confidence === 'number') {
      return confidence.toFixed(2); // Display confidence with 2 decimal places
    }
    return <span className="text-base-content/50 italic">N/A</span>;
  };

  // Get all analysis fields that are present
  const analysisFields = result?.analysis ? Object.entries(result.analysis).filter(([key, field]) =>
    field && typeof field === 'object' && field.present === true
  ) : [];

  // Fallback if no valid data to display after filtering
  if (analysisFields.length === 0) {
    return (
      <div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
        <div className="flex items-center gap-2 text-info">
          <PhosphorIcons.Info size={20} weight="bold" />
          <p className="font-semibold">No food specification properties were found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      <div className="overflow-x-auto border border-base-300 rounded-lg shadow-sm bg-base-100">
        <table className="table table-zebra w-full">
          {/* Head */}
          <thead className="bg-base-200">
            <tr>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Property</th>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Raw Value</th>
              <th className="p-3 text-left text-sm font-semibold text-base-content tracking-wider">Value</th>
              <th className="p-3 text-center text-sm font-semibold text-base-content tracking-wider">Present</th>
              <th className="p-3 text-right text-sm font-semibold text-base-content tracking-wider">Confidence</th>
            </tr>
          </thead>
          <tbody>
          {analysisFields
            .sort(([fieldNameA], [fieldNameB]) => fieldNameA.localeCompare(fieldNameB))
            .map(([fieldName, field]) => {
              if (!field || typeof field !== 'object') return null;

              return (
                <tr key={fieldName} className="hover:bg-base-200/50">
                  {/* Property Name */}
                  <td className="p-3 text-sm font-medium text-base-content whitespace-nowrap">{fieldName}</td>
                  {/* Raw Value */}
                  <td className="p-3 text-sm text-base-content/80">{formatValueForTable(field.raw)}</td>
                  {/* Value */}
                  <td className="p-3 text-sm text-base-content/80">{formatValueForTable(field.value)}</td>
                  {/* Present */}
                  <td className="p-3 text-sm text-center text-base-content/80">{formatValueForTable(field.present)}</td>
                  {/* Confidence */}
                  <td className="p-3 text-sm text-right text-base-content/80">{formatConfidence(field.confidence)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FoodSpecificationViewResult; 